import { createAsyncThunk } from '@reduxjs/toolkit';
import { getAggregatePartnerAPI, validateTransferPartnerAPI } from '@services/WebApiService';

async function validateByRegion({ accessToken, reset, parameters, region }) {
  try {
    const result = await validateTransferPartnerAPI({
      accessToken,
      reset,
      parameters,
      region,
    });

    return { region, parameters, validateResult: result.data };
  } catch (error) {
    console.error(error);
    let errorMessage;
    if (error.response?.data?.message) errorMessage = error.response?.data?.message;
    else if (error.response?.status) errorMessage = `Error ${error.response?.status}`;
    else errorMessage = 'Something went wrong when validating the request.';
    return { region, parameters, errorMessage };
  }
}

const validateTransferAggregatePartnerAsync = createAsyncThunk(
  'TransferPartner/validateTransferAggregatePartner',
  async (paramsList) => {
    try {
      const { accessToken, reset, parameters } = paramsList;

      // Get aggregate
      const resellerIDToBeTransferred = Number.parseInt(parameters.resellerIDToBeTransferred, 10);
      const targetDistributorID = Number.parseInt(parameters.targetDistributorID, 10);
      const sourceDistributorID = Number.parseInt(parameters.sourceDistributorID, 10);
      const getAggregateParameters = {
        resellerToBeTransfered: Number.isNaN(resellerIDToBeTransferred)
          ? undefined
          : resellerIDToBeTransferred,
        targetDistributor: Number.isNaN(targetDistributorID) ? undefined : targetDistributorID,
        sourceDistributor: Number.isNaN(sourceDistributorID) ? undefined : sourceDistributorID,
      };
      const aggregateResult = await getAggregatePartnerAPI({
        accessToken,
        reset,
        parameters: getAggregateParameters,
      });

      if (!aggregateResult?.data) {
        return { error: 'Failed to get reseller infomation' };
      }

      // If all of the resellers are not aggregate
      // Validate normally
      if (!aggregateResult.data.resellerToBeTransfered?.['is-aggregate']) {
        const validateResult = await validateTransferPartnerAPI(paramsList);
        return {
          isAggregate: false,
          results: [{ parameters, validateResult: validateResult.data }],
        };
      }

      if (
        !aggregateResult.data.resellerToBeTransfered?.['is-aggregate'] ||
        !aggregateResult.data.resellerToBeTransfered?.identities
      ) {
        return { error: 'Cannot transfer non-aggregate partner to an aggregate partner' };
      }

      // Parse regions from localStorage
      const regions = {};
      try {
        const regionList = JSON.parse(localStorage.INT_STORAGE_REGIONS_LIST);
        regionList.forEach((r) => {
          regions[r.regionId] = r.regionCode;
        });
      } catch {
        return { error: 'Failed getting regions' };
      }

      const validateParamsList = [];
      for (let i = 0; i < aggregateResult.data.resellerToBeTransfered.identities.length; i += 1) {
        const currentIdentity = aggregateResult.data.resellerToBeTransfered.identities[i];

        // Get the region
        const region = regions[currentIdentity['region-id']];
        if (!region) {
          return { error: 'Failed getting region' };
        }

        // Source reseller identity
        let sourceIdentity;
        if (getAggregateParameters.sourceDistributor) {
          sourceIdentity = aggregateResult.data.sourceDistributor?.identities?.find(
            (identity) => identity['region-id'] === currentIdentity['region-id']
          );
          if (!sourceIdentity) {
            return {
              error: 'Cannot transfer aggregate partner from a non-aggregate source distributor',
            };
          }
        }

        // Destination reseller identity
        let targetIdentity;
        if (getAggregateParameters.targetDistributor) {
          targetIdentity = aggregateResult.data.targetDistributor?.identities?.find(
            (identity) => identity['region-id'] === currentIdentity['region-id']
          );
          if (!targetIdentity) {
            return {
              error: 'Cannot transfer aggregate partner to a non-aggregate target distributor',
            };
          }
        }

        const currentParameters = {
          ...parameters,
          resellerIDToBeTransferred: `${currentIdentity['reseller-id']}`,
          sourceDistributorID: sourceIdentity?.['reseller-id']
            ? `${sourceIdentity?.['reseller-id']}`
            : undefined,
          targetDistributorID: targetIdentity?.['reseller-id']
            ? `${targetIdentity?.['reseller-id']}`
            : undefined,
        };
        validateParamsList.push({ accessToken, reset, parameters: currentParameters, region });
      }

      const promises = [];
      for (let i = 0; i < validateParamsList.length; i += 1) {
        const currentParamsList = validateParamsList[i];
        const promise = validateByRegion(currentParamsList);
        promises.push(promise);
      }

      const results = await Promise.all(promises);
      return { isAggregate: true, results };
    } catch (error) {
      console.error(error);
      if (error.response?.data?.message) return { error: error.response?.data?.message };
      if (error.response?.status) return { error: `Error ${error.response?.status}` };
      return { error: 'Something went wrong when processing your request' };
    }
  }
);

const { pending, fulfilled, rejected } = validateTransferAggregatePartnerAsync;

const validateTransferAggregatePartnerExtraReducers = {
  [pending]: (state) => {
    state.transferAggregatePartnerState.errorMessage = null;
    state.transferAggregatePartnerState.results = null;
    state.transferAggregatePartnerState.inProgress = true;
  },
  [rejected]: (state) => {
    state.transferAggregatePartnerState.inProgress = false;
  },
  [fulfilled]: (state, action) => {
    state.transferAggregatePartnerState.inProgress = false;
    state.transferAggregatePartnerState.errorMessage = action?.payload?.error || null;
    state.transferAggregatePartnerState.results = action?.payload?.results || null;
    state.transferAggregatePartnerState.isAggregate = action?.payload?.isAggregate || null;
  },
};

export { validateTransferAggregatePartnerAsync, validateTransferAggregatePartnerExtraReducers };
